import type { CSSProperties } from 'react';
import { Extension } from '@tiptap/core';

import '@tiptap/extension-text-style';

import { isSupportedTextColor } from '../../Controls/constants';

type ColorOptions = {
  types: string[];
};

// Read the raw style attribute rather than `element.style.color`, which reports every colour as
// `rgb(...)` and so would lose the original hex on parse.
//
// The serializer writes our rendered style string via `element.style.cssText`, and CSSOM rewrites
// colours to `rgb(...)`. So `getHTML()` — the string that gets persisted — always comes back as
// `rgb(...)`, whatever we stored. Both forms therefore have to parse, normalised to hex so that hex
// stays the single stored format the PDF Lambda reads.
const COLOR_DECLARATION_REGEX = /(?:^|;)\s*color\s*:\s*([^;]+)/i;
const HEX_COLOR_REGEX = /^#[0-9a-f]{6}$/;
// Accepts the legacy comma-separated and the CSS Color 4 space-separated forms.
const RGB_COLOR_REGEX = /^rgb\(\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*\)$/;

const toHexColor = (value: string): string | null => {
  if (HEX_COLOR_REGEX.test(value)) return value;

  const rgb = RGB_COLOR_REGEX.exec(value);
  if (!rgb) return null;
  const channels = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  if (channels.some((channel) => channel > 255)) return null;

  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

const extractStyleColor = (element: HTMLElement): string | null => {
  const style = element.getAttribute('style');
  if (!style) return null;
  const match = COLOR_DECLARATION_REGEX.exec(style);
  if (!match) return null;
  return toHexColor(match[1].trim().toLowerCase());
};

// Tint a listItem's marker only when the colour command covers the
// item's entire OWN LINE — its first text block, the line the marker
// renders against. Later paragraphs and nested sub-lists never gate
// the marker. Partial selections leave the marker alone, which makes
// the attribute sticky across later typing/pasting — those commands
// don't run here so they can't change it.
//
// Every item whose own line is fully covered is tinted, including an
// item that wraps a nested list (e.g. the line "in front of" an inner
// list): selecting just a deep child never counts as covering its
// ancestor's line. CSS stops the `--marker-color` custom property from
// cascading into nested lists, so colouring such an item doesn't bleed
// onto its children's markers.
const applyMarkerToFullyCoveredItems = (tr: any, state: any, nextColor: string | null): void => {
  const listItemType = state.schema.nodes.listItem;
  if (!listItemType) return;

  state.selection.ranges.forEach((range: any) => {
    const selStart = range.$from.pos;
    const selEnd = range.$to.pos;

    state.doc.nodesBetween(selStart, selEnd, (node: any, pos: number) => {
      if (node.type !== listItemType) return undefined;

      // The own line is the item's FIRST text block — the line the
      // marker renders against. Later paragraphs and any nested
      // sub-list are not part of that line, so they never gate the
      // marker: an item whose continuation paragraph stays default can
      // still have its marker coloured by covering just the first line.
      const ownLine = node.childCount > 0 ? node.child(0) : null;
      if (!ownLine || !ownLine.isTextblock) return undefined;
      const ownStart = pos + 1;
      const ownEnd = ownStart + ownLine.nodeSize;

      let allCovered = true;
      let sawText = false;
      ownLine.descendants((child: any, relPos: number) => {
        if (!allCovered) return false;
        if (!child.isText) return undefined;
        sawText = true;
        const absStart = ownStart + 1 + relPos;
        const absEnd = absStart + child.nodeSize;
        if (absStart < selStart || absEnd > selEnd) allCovered = false;
        return undefined;
      });

      // An empty own line has no text to gate on, so allCovered is
      // vacuously true. Require the selection to actually reach the own
      // line; otherwise selecting a deep child would tint its empty
      // ancestor's marker.
      const ownLineReached = sawText || (selStart <= ownEnd && selEnd >= ownStart);

      if (allCovered && ownLineReached && node.attrs.markerColor !== nextColor) {
        tr.setNodeAttribute(pos, 'markerColor', nextColor);
      }
      return undefined;
    });
  });
};

export const Color = Extension.create<ColorOptions>({
  name: 'color',

  addOptions(): ColorOptions {
    return {
      types: ['textStyle'],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          color: {
            default: null,
            parseHTML: extractStyleColor,
            renderHTML: (attributes: CSSProperties) => {
              if (!attributes.color) {
                return {};
              }
              return {
                style: `color: ${attributes.color}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setColor:
        (color: string) =>
        ({ chain, tr, state, dispatch }: any) => {
          if (!isSupportedTextColor(color)) {
            return false;
          }
          if (dispatch) {
            applyMarkerToFullyCoveredItems(tr, state, color);
          }
          return chain().setMark('textStyle', { color }).run();
        },
      unsetColor:
        () =>
        ({ chain, tr, state, dispatch }: any) => {
          if (dispatch) {
            applyMarkerToFullyCoveredItems(tr, state, null);
          }
          return chain().setMark('textStyle', { color: null }).removeEmptyTextStyle().run();
        },
    };
  },
});
